import React, { Component, useState, useEffect, useRef } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../../stripe";
import axiosInstance from "../../../axios-instance";
import { getAdminBusinessId } from "../../../utils/authHelper";

/* ─── Design tokens (MeetOcto light theme) ───────────────────────────── */
const C = {
  orange: "#FE9920",
  orangeLight: "#FFF7ED",
  orangeSoft: "#FFEDD5",
  orangeDark: "#C2410C",
  dark: "#111827",
  mid: "#374151",
  muted: "#6B7280",
  border: "#E5E7EB",
  green: "#10B981",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  greenDark: "#065F46",
  teal: "#2DD4BF",
  tealTrack: "#E5E7EB",
  card: "#fff",
  surface: "#F9FAFB",
  danger: "#EF4444",
};

/** Personal allotment granted with paid subscription (matches backend MONTHLY_CREDIT_POINTS). */
const PERSONAL_INCLUDED = 250;

const S = {
  wrapper: { padding: "24px" },
  balanceRow: {
    display: "flex",
    gap: 20,
    marginBottom: 28,
    flexWrap: "wrap",
  },
  balanceCard: (color = C.orange) => ({
    flex: "1 1 220px",
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "20px 24px",
    borderTop: `4px solid ${color}`,
  }),
  balanceLabel: { fontSize: 13, color: C.muted, fontWeight: 500, marginBottom: 6 },
  balanceValue: { fontSize: 32, fontWeight: 800, color: C.dark },
  balanceUnit: { fontSize: 14, color: C.muted, fontWeight: 400, marginLeft: 4 },
  section: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "24px",
    marginBottom: 24,
  },
  dualRow: {
    display: "flex",
    alignItems: "stretch",
    gap: 16,
    marginBottom: 24,
    flexWrap: "wrap",
  },
  dualCol: {
    flex: "1 1 340px",
    minWidth: 0,
    display: "flex",
  },
  sectionInRow: {
    background: "#fff",
    border: `1px solid ${C.border}`,
    borderRadius: 14,
    padding: "20px",
    marginBottom: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  sectionTitle: { fontSize: 17, fontWeight: 700, color: C.dark, marginBottom: 6 },
  sectionSub: { fontSize: 13, color: C.muted, marginBottom: 20 },
  packagesGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  pkgCard: (selected) => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 132,
    maxWidth: 160,
    border: `1.5px solid ${selected ? C.orange : C.border}`,
    borderRadius: 10,
    padding: "12px 14px",
    cursor: "pointer",
    background: selected ? C.orangeLight : "#fff",
    boxShadow: selected ? "0 0 0 1px #FE9920" : "none",
    transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
    position: "relative",
  }),
  pkgName: {
    fontSize: 11,
    fontWeight: 600,
    color: C.muted,
    marginBottom: 4,
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pkgCredits: { fontSize: 20, fontWeight: 800, color: C.dark, lineHeight: 1.1 },
  pkgLabel: { fontSize: 11, color: C.muted, marginTop: 2, marginBottom: 8 },
  pkgPrice: { fontSize: 14, fontWeight: 700, color: C.orange, lineHeight: 1 },
  pkgCheck: {
    position: "absolute",
    top: 6,
    right: 6,
    background: C.orange,
    color: "#fff",
    borderRadius: "50%",
    width: 16,
    height: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 10,
    fontWeight: 700,
  },
  cardBox: {
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    padding: "14px 16px",
    background: "#fff",
    marginBottom: 16,
  },
  btn: (variant = "primary", disabled = false) => ({
    padding: "11px 28px",
    borderRadius: 9,
    fontSize: 14,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    border: "none",
    background: disabled ? "#D1D5DB" : variant === "primary" ? C.orange : C.surface,
    color: disabled ? "#9CA3AF" : variant === "primary" ? "#fff" : C.mid,
    marginRight: 10,
    opacity: disabled ? 0.8 : 1,
  }),
  txTable: { width: "100%", borderCollapse: "collapse", marginTop: 8 },
  txTh: {
    textAlign: "left",
    padding: "8px 12px",
    background: C.surface,
    fontSize: 12,
    fontWeight: 600,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    borderBottom: `1px solid ${C.border}`,
  },
  txTd: { padding: "10px 12px", fontSize: 13, color: C.mid, borderBottom: `1px solid #F3F4F6` },
  badge: (type) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    background: type === "credit" ? "#D1FAE5" : "#FEE2E2",
    color: type === "credit" ? C.greenDark : "#991B1B",
  }),
  emptyTx: { textAlign: "center", padding: "24px 0", color: C.muted, fontSize: 14 },
  successBox: {
    background: C.greenBg,
    border: `1px solid ${C.greenBorder}`,
    borderRadius: 10,
    padding: "14px 18px",
    color: C.greenDark,
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 16,
  },
};

function formatActivityDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const datePart = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${datePart} · ${timePart}`;
}

function csvEscape(value) {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadTeamActivityCsv(transactions) {
  const headers = [
    "Date and time",
    "Activity",
    "Change",
    "Balance before",
    "Balance after",
    "Amount",
    "By",
  ];
  const rows = transactions.map((tx) => {
    const isCredit = tx.type === "credit";
    const change = `${isCredit ? "+" : "-"}${Number(tx.points || 0)}`;
    const amount =
      tx.amount_usd != null && isCredit
        ? `$${Number(tx.amount_usd).toFixed(2)}`
        : "";
    return [
      formatActivityDate(tx.created_at),
      tx.activity || (isCredit ? "Credit purchase" : "SMS usage"),
      change,
      tx.balance_before ?? "",
      tx.balance_after ?? "",
      amount,
      tx.user || "",
    ];
  });
  const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `team-credit-activity-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function packageOptionLabel(pkg) {
  if (!pkg) return "Select a package";
  return `${Number(pkg.credits || 0).toLocaleString()} credits — $${Number(pkg.price || 0).toFixed(0)}`;
}

/* ─── Add team credits (structure from mock; MeetOcto light theme) ─────── */
const AddTeamCreditsPanel = ({
  packages,
  loading,
  selectedPackage,
  onSelectPackage,
  onContinue,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);
  const credits = selectedPackage ? Number(selectedPackage.credits || 0) : 0;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  return (
    <div style={S.sectionInRow}>
      {loading ? (
        <div style={{ color: C.muted, fontSize: 14 }}>Loading packages…</div>
      ) : packages.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 14 }}>
          No SMS credit packages are available at this time.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ ...S.sectionTitle, marginBottom: 6 }}>Add team credits</div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>
            Shared by your whole team
          </div>
          <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.45, marginBottom: 18 }}>
            Up to{" "}
            <strong style={{ color: C.dark, fontWeight: 700 }}>
              {credits > 0 ? credits.toLocaleString() : "—"}
            </strong>{" "}
            US/Canada SMS segments. Usage varies by destination and message length.
          </div>

          <div ref={wrapRef} style={{ marginTop: "auto", position: "relative" }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: C.muted, marginBottom: 8 }}>
              Credit package
            </div>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={menuOpen}
              style={{
                width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
                gap: 10,
                background: "#fff",
                border: `1.5px solid ${menuOpen ? C.orange : C.border}`,
                borderRadius: 10,
                padding: "12px 14px",
    cursor: "pointer",
                textAlign: "left",
                marginBottom: 12,
                boxShadow: menuOpen ? "0 0 0 1px #FE9920" : "none",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 600, color: C.dark }}>
                {packageOptionLabel(selectedPackage)}
              </span>
              <span style={{ color: C.muted, fontSize: 12, flexShrink: 0 }} aria-hidden="true">
                {menuOpen ? "▲" : "▼"}
              </span>
            </button>

            {menuOpen && (
              <ul
                role="listbox"
                style={{
                  position: "absolute",
                  zIndex: 25,
                  left: 0,
                  right: 0,
                  bottom: 56,
                  margin: 0,
                  padding: 6,
                  listStyle: "none",
                  background: "#fff",
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  boxShadow: "0 12px 28px rgba(17, 24, 39, 0.12)",
                  maxHeight: 220,
                  overflowY: "auto",
                }}
              >
                {packages.map((pkg) => {
                  const active = selectedPackage?.id === pkg.id;
                  return (
                    <li key={pkg.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onSelectPackage(pkg);
                          setMenuOpen(false);
                        }}
                        style={{
                          width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
                          gap: 10,
                          border: "none",
                          background: active ? C.orangeLight : "transparent",
                          borderRadius: 8,
                          padding: "11px 12px",
                          cursor: "pointer",
                          textAlign: "left",
                          fontSize: 14,
                          fontWeight: active ? 700 : 500,
                          color: C.dark,
                        }}
                      >
                        <span>{packageOptionLabel(pkg)}</span>
                        {active && (
                          <span style={{ color: C.orange, fontWeight: 700 }} aria-hidden="true">
                            ✓
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <button
              type="button"
              onClick={onContinue}
              disabled={!selectedPackage}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: 10,
                border: "none",
                background: selectedPackage ? C.orange : "#D1D5DB",
                color: selectedPackage ? "#fff" : "#9CA3AF",
                fontSize: 15,
                fontWeight: 700,
                cursor: selectedPackage ? "pointer" : "not-allowed",
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Team credit activity (structure from mock; MeetOcto light theme) ─── */
const TeamCreditActivity = ({ balance, transactions, summary, loading }) => {
  const totalPurchased = summary?.total_purchased ?? 0;
  const totalUsed = summary?.total_used ?? 0;
  const totalPaid = summary?.total_paid ?? 0;

  const stats = [
    {
      label: "Total purchased",
      value: `${Number(totalPurchased).toLocaleString()} credits`,
    },
    {
      label: "Total paid",
      value: `$${Number(totalPaid).toFixed(2)}`,
    },
    {
      label: "Total used",
      value: `${Number(totalUsed).toLocaleString()} credits`,
    },
    {
      label: "Current balance",
      value: `${Number(balance || 0).toLocaleString()} credits`,
    },
  ];

  return (
    <div style={S.section}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div style={{ ...S.sectionTitle, marginBottom: 6 }}>Team credit activity</div>
          <div style={{ ...S.sectionSub, marginBottom: 0 }}>
            Every purchase, refund, adjustment, and SMS usage affecting your balance.
          </div>
        </div>
        <button
          type="button"
          onClick={() => downloadTeamActivityCsv(transactions)}
          disabled={loading || !transactions.length}
          style={{
            padding: "9px 16px",
            borderRadius: 9,
            fontSize: 13,
            fontWeight: 600,
            background: "#fff",
            color: C.mid,
            border: `1.5px solid ${C.border}`,
            cursor: loading || !transactions.length ? "not-allowed" : "pointer",
            opacity: loading || !transactions.length ? 0.55 : 1,
            whiteSpace: "nowrap",
          }}
        >
          Download CSV
        </button>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 20,
              background: "#fff",
            }}
          >
            {stats.map((stat, i) => (
              <div
                key={stat.label}
                style={{
                  padding: "16px 18px",
                  borderTop: i >= 2 ? `1px solid ${C.border}` : "none",
                  borderLeft: i % 2 === 1 ? `1px solid ${C.border}` : "none",
                }}
              >
                <div style={{ fontSize: 12, color: C.muted, fontWeight: 500, marginBottom: 6 }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.dark, lineHeight: 1.2 }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {transactions.length === 0 ? (
            <div style={S.emptyTx}>No team credit activity yet.</div>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 12 }}>
              <table style={{ ...S.txTable, marginTop: 0 }}>
                <thead>
                  <tr>
                    {[
                      "Date and time",
                      "Activity",
                      "Change",
                      "Balance before",
                      "Balance after",
                      "Amount",
                    ].map((h) => (
                      <th key={h} style={{ ...S.txTh, textTransform: "none", letterSpacing: 0 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx) => {
                    const isCredit = tx.type === "credit";
                    return (
                      <tr key={tx.id}>
                        <td style={S.txTd}>{formatActivityDate(tx.created_at)}</td>
                        <td style={{ ...S.txTd, fontWeight: 600, color: C.dark }}>
                          {tx.activity || (isCredit ? "Credit purchase" : "SMS usage")}
                        </td>
                        <td style={S.txTd}>
                          <span
                            style={{
                              color: isCredit ? C.greenDark : C.danger,
                              fontWeight: 700,
                            }}
                          >
                            {isCredit ? "+" : "-"}
                            {Number(tx.points || 0).toLocaleString()}
                          </span>
                        </td>
                        <td style={S.txTd}>
                          {tx.balance_before != null
                            ? Number(tx.balance_before).toLocaleString()
                            : "—"}
                        </td>
                        <td style={S.txTd}>
                          {tx.balance_after != null
                            ? Number(tx.balance_after).toLocaleString()
                            : "—"}
                        </td>
                        <td style={S.txTd}>
                          {tx.amount_usd != null && isCredit
                            ? `$${Number(tx.amount_usd).toFixed(2)}`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

function memberInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function memberRoleLabel(m) {
  const role = m?.role === "admin" ? "Admin" : "Staff";
  if (m?.is_current) return `You · ${role}`;
  return role;
}

/* ─── Member SMS credits panel (structure from mock; MeetOcto light theme) ─ */
const MemberSmsCreditsPanel = ({ members, loading }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!members?.length) {
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && members.some((m) => m.user_id === prev)) return prev;
      const current = members.find((m) => m.is_current);
      return current?.user_id ?? members[0].user_id;
    });
  }, [members]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const selected = members.find((m) => m.user_id === selectedId) || members[0];
  const left = selected ? Math.max(0, Number(selected.balance) || 0) : 0;
  const included = PERSONAL_INCLUDED;
  const used = Math.max(0, included - Math.min(left, included));
  const remainingPct = included > 0 ? Math.round((Math.min(left, included) / included) * 100) : 0;
  const usingPersonal = left > 0;

  return (
    <div style={S.sectionInRow}>
      <div style={{ ...S.sectionTitle, marginBottom: 6 }}>Member SMS credits</div>
      <div style={{ ...S.sectionSub, marginBottom: 14 }}>
        Each member receives {PERSONAL_INCLUDED} personal credits with their subscription. Select a
        member to view their usage.
      </div>

      {loading ? (
        <div style={{ ...S.emptyTx, padding: "12px 0" }}>Loading balances…</div>
      ) : !members.length ? (
        <div style={{ ...S.emptyTx, padding: "12px 0" }}>No team members found.</div>
      ) : (
        <>
          {/* Member selector row */}
          <div
            ref={wrapRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              padding: "14px 16px",
              background: C.surface,
              marginBottom: 18,
              position: "relative",
            }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: C.orange,
                color: "#fff",
                fontWeight: 700,
                fontSize: 14,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {memberInitials(selected?.name)}
            </div>

            <div style={{ flex: "1 1 200px", minWidth: 0, position: "relative" }}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 10,
                  background: "#fff",
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 700, color: C.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {selected?.name || "Select member"}
                  <span style={{ fontWeight: 500, color: C.muted }}>
                    {" "}
                    ({memberRoleLabel(selected)})
                  </span>
                </span>
                <span style={{ color: C.muted, fontSize: 12, flexShrink: 0 }} aria-hidden="true">
                  {menuOpen ? "▲" : "▼"}
                </span>
              </button>
              {selected?.email && (
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6, paddingLeft: 2 }}>
                  {selected.email}
                </div>
              )}

              {menuOpen && (
                <ul
                  role="listbox"
                  style={{
                    position: "absolute",
                    zIndex: 20,
                    left: 0,
                    right: 0,
                    top: "calc(100% + 4px)",
                    margin: 0,
                    padding: 6,
                    listStyle: "none",
                    background: "#fff",
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    boxShadow: "0 10px 28px rgba(17, 24, 39, 0.12)",
                    maxHeight: 240,
                    overflowY: "auto",
                  }}
                >
                  {members.map((m) => {
                    const active = m.user_id === selected?.user_id;
                    return (
                      <li key={m.user_id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => {
                            setSelectedId(m.user_id);
                            setMenuOpen(false);
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            border: "none",
                            background: active ? C.orangeLight : "transparent",
                            borderRadius: 8,
                            padding: "10px 10px",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <span
                            style={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              background: C.orangeSoft,
                              color: C.orangeDark,
                              fontSize: 11,
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {memberInitials(m.name)}
                          </span>
                          <span style={{ minWidth: 0, flex: 1 }}>
                            <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: C.dark }}>
                              {m.name}
                              <span style={{ fontWeight: 500, color: C.muted }}>
                                {" "}
                                ({memberRoleLabel(m)})
                              </span>
                            </span>
                            {m.email && (
                              <span style={{ display: "block", fontSize: 11, color: C.muted }}>
                                {m.email}
                              </span>
                            )}
                          </span>
                          <span style={{ fontSize: 12, fontWeight: 700, color: C.dark, flexShrink: 0 }}>
                            {Number(m.balance || 0).toLocaleString()}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginLeft: 0,
                marginTop: 4,
                padding: "6px 10px",
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 600,
                background: usingPersonal ? C.greenBg : C.orangeLight,
                color: usingPersonal ? C.greenDark : C.orangeDark,
                border: `1px solid ${usingPersonal ? C.greenBorder : "#FED7AA"}`,
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: usingPersonal ? C.green : C.orange,
                  flexShrink: 0,
                }}
              />
              {usingPersonal ? "Using personal credits" : "Using shared organization credits"}
            </span>
          </div>

          {/* Stats grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 0,
              border: `1px solid ${C.border}`,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 18,
              background: "#fff",
            }}
          >
            {[
              {
                label: "Included",
                value: `${included.toLocaleString()}`,
              },
              { label: "Used", value: used.toLocaleString() },
              { label: "Left", value: left.toLocaleString() },
            ].map((stat, i) => (
              <div
                key={stat.label}
                style={{
                  padding: "12px 10px",
                  borderLeft: i === 0 ? "none" : `1px solid ${C.border}`,
                }}
              >
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 500 }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.dark, lineHeight: 1.15 }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          {/* Progress */}
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 8,
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 600, color: C.dark }}>
                {Math.min(left, included).toLocaleString()} of {included.toLocaleString()} left
              </span>
              <span style={{ color: C.muted, fontWeight: 500 }}>{remainingPct}% remaining</span>
            </div>
            <div
              style={{
                height: 10,
                borderRadius: 9999,
                background: C.tealTrack,
                overflow: "hidden",
              }}
              role="progressbar"
              aria-valuenow={remainingPct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div
                style={{
                  width: `${remainingPct}%`,
                  height: "100%",
                  borderRadius: 9999,
                  background: remainingPct === 0 ? "transparent" : usingPersonal ? C.teal : C.orange,
                  transition: "width 0.25s ease",
                }}
              />
            </div>
          </div>

          {/* Usage order footer */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: C.mid,
              lineHeight: 1.4,
              marginTop: "auto",
            }}
          >
            <span style={{ color: C.muted, fontWeight: 500 }}>SMS reminders use:</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600, color: C.dark }}>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: C.orange,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                1
              </span>
              Personal credits
            </span>
            <span style={{ color: C.muted }} aria-hidden="true">
              →
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600, color: C.dark }}>
              <span
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  background: C.orange,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                2
              </span>
              Shared credits
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Card form (Stripe Elements) ─────────────────────────────────────── */
const CARD_STYLE = {
  style: {
    base: {
      fontSize: "15px",
      color: C.dark,
      fontFamily: "Inter, -apple-system, sans-serif",
      "::placeholder": { color: "#9CA3AF" },
    },
    invalid: { color: C.danger },
  },
  hidePostalCode: true,
};

const SMSCheckoutForm = ({ selectedPackage, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements || !selectedPackage) return;
    setProcessing(true);
    setCardError(null);

    try {
      // 1. Create PaymentIntent on backend
      const piRes = await axiosInstance.post("admin/sms-credit/create-payment-intent", {
        package_id: selectedPackage.id,
      });
      const { client_secret, payment_intent_id } = piRes.data?.data || {};

      if (!client_secret) throw new Error("Could not start payment. Please try again.");

      // 2. Confirm card payment with Stripe.js
      const { error, paymentIntent } = await stripe.confirmCardPayment(client_secret, {
        payment_method: { card: elements.getElement(CardElement) },
      });

      if (error) {
        setCardError(error.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        // 3. Notify backend to credit the team wallet
        const confirmRes = await axiosInstance.post("admin/sms-credit/confirm-purchase", {
          payment_intent_id,
          package_id: selectedPackage.id,
        });
        const { team_balance } = confirmRes.data?.data || {};
        onSuccess(team_balance, selectedPackage.credits);
      } else {
        setCardError("Payment not confirmed. Please try again.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Payment failed. Please try again.";
      setCardError(msg);
    }
    setProcessing(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.mid, marginBottom: 8 }}>Card Details</div>
        <div style={S.cardBox}>
          <CardElement options={CARD_STYLE} onChange={(e) => e.error && setCardError(e.error.message)} />
        </div>
        {cardError && (
          <div style={{ color: C.danger, fontSize: 13, marginBottom: 10 }}>{cardError}</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontSize: 14, color: C.muted }}>
          Charging <strong style={{ color: C.dark }}>${Number(selectedPackage.price).toFixed(2)}</strong> for{" "}
          <strong style={{ color: C.dark }}>{selectedPackage.credits.toLocaleString()} SMS credits</strong>
        </div>
        <div>
          <button style={S.btn("secondary")} onClick={onCancel} disabled={processing}>Cancel</button>
          <button style={S.btn("primary", processing || !stripe)} onClick={handlePay} disabled={processing || !stripe}>
            {processing ? "Processing..." : `Pay $${Number(selectedPackage.price).toFixed(2)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main SMS Credits Tab ─────────────────────────────────────────────── */
class SMSCreditsTab extends Component {
  state = {
    packages: [],
    balance: 0,
    transactions: [],
    members: [],
    loadingPackages: true,
    loadingWallet: true,
    loadingMembers: true,
    selectedPackage: null,
    showCheckout: false,
    lastSuccess: null,
    walletSummary: { total_purchased: 0, total_used: 0, total_paid: 0 },
  };

  componentDidMount() {
    this.loadPackages();
    this.loadWallet();
    this.loadMembers();
  }

  loadPackages = () => {
    this.setState({ loadingPackages: true });
    axiosInstance.get("admin/sms-credit-packages")
      .then((res) => {
        const packages = res.data?.data?.packages || [];
        this.setState((prev) => ({
          packages,
          loadingPackages: false,
          selectedPackage:
            prev.selectedPackage &&
            packages.some((p) => p.id === prev.selectedPackage.id)
              ? prev.selectedPackage
              : packages[0] || null,
        }));
      })
      .catch(() => this.setState({ loadingPackages: false }));
  };

  loadWallet = () => {
    this.setState({ loadingWallet: true });
    axiosInstance.get("admin/sms-team-wallet")
      .then(res => {
        const data = res.data?.data || {};
        this.setState({
          balance: data.balance || 0,
          transactions: data.transactions || [],
          walletSummary: data.summary || { total_purchased: 0, total_used: 0, total_paid: 0 },
          loadingWallet: false,
        });
      })
      .catch(() => this.setState({ loadingWallet: false }));
  };

  loadMembers = () => {
    this.setState({ loadingMembers: true });
    const businessId = getAdminBusinessId();
    const url = businessId
      ? `admin/sms-member-balances?business_id=${businessId}`
      : "admin/sms-member-balances";

    axiosInstance.get(url)
      .then(res => {
        const payload = res?.data?.data ?? res?.data ?? {};
        const members = Array.isArray(payload.members) ? payload.members : [];
        this.setState({ members, loadingMembers: false });
      })
      .catch((err) => {
        console.error("Failed to load SMS member balances", err);
        this.setState({ members: [], loadingMembers: false });
      });
  };

  selectPackage = (pkg) => {
    this.setState({ selectedPackage: pkg, lastSuccess: null });
  };

  continueToCheckout = () => {
    if (!this.state.selectedPackage) return;
    this.setState({ showCheckout: true, lastSuccess: null });
  };

  onPaymentSuccess = (teamBalance, creditsAdded) => {
    this.setState((prev) => ({
      showCheckout: false,
      lastSuccess: creditsAdded,
      balance: teamBalance != null ? teamBalance : prev.balance + creditsAdded,
      selectedPackage: prev.packages[0] || prev.selectedPackage,
    }));
    this.loadWallet();
  };

  cancelCheckout = () => this.setState({ showCheckout: false });

  render() {
    const {
      packages, balance, transactions, members, walletSummary,
      loadingPackages, loadingWallet, loadingMembers,
      selectedPackage, showCheckout, lastSuccess,
    } = this.state;

    return (
      <div style={S.wrapper}>
        {/* Balance Cards */}
        <div style={S.balanceRow}>
          <div style={S.balanceCard(C.orange)}>
            <div style={S.balanceLabel}>Team Shared Balance</div>
            <div style={S.balanceValue}>
              {loadingWallet ? "—" : balance.toLocaleString()}
              <span style={S.balanceUnit}>credits</span>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
              Shared across your whole team — admin + staff
            </div>
          </div>
        </div>

        <div style={S.dualRow}>
          <div style={S.dualCol}>
            <MemberSmsCreditsPanel members={members} loading={loadingMembers} />
              </div>
          <div style={S.dualCol}>
            <AddTeamCreditsPanel
              packages={packages}
              loading={loadingPackages}
              selectedPackage={selectedPackage}
              onSelectPackage={this.selectPackage}
              onContinue={this.continueToCheckout}
            />
            </div>
        </div>

        {/* Success */}
        {lastSuccess && (
          <div style={S.successBox}>
            ✓ {lastSuccess.toLocaleString()} SMS credits added to your team wallet!
          </div>
        )}

        {/* Checkout */}
        {showCheckout && selectedPackage && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Complete Purchase</div>
            <div style={{ ...S.sectionSub, marginBottom: 16 }}>
              Buying <strong>{selectedPackage.credits.toLocaleString()} SMS credits</strong> for your team wallet
            </div>
            <Elements stripe={stripePromise} options={{ appearance: { theme: "stripe" } }}>
              <SMSCheckoutForm
                selectedPackage={selectedPackage}
                onSuccess={this.onPaymentSuccess}
                onCancel={this.cancelCheckout}
              />
            </Elements>
          </div>
        )}

        {/* Team credit activity */}
        <TeamCreditActivity
          balance={balance}
          transactions={transactions}
          summary={walletSummary}
          loading={loadingWallet}
        />
      </div>
    );
  }
}

export default SMSCreditsTab;
